import React, { useEffect, useMemo, useState } from "react";
import {
  Layers, Upload, FileSpreadsheet, Eye, Trash2, Filter, Search, Download,
  Calculator, ShieldCheck, AlertTriangle, FileBarChart, Calendar, MapPin, Package,
  Loader2, RefreshCw, Activity as ActivityIcon, ClipboardList, X,
} from "lucide-react";
import { toast } from "sonner";
import { useApp, can } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  spUploadConsumption, spListConsumption, spDeleteConsumption,
  spCalculateABCD, spGetABCD,
  spGenerateSuggested, spGetSuggested,
  spVerifyOrder, spGetVerification,
  spDashboard, spExportSuggested, spExportVerification,
  spActivity, spActivityUsers,
} from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: FileBarChart },
  { key: "consumption", label: "Consumption Analysis", icon: Calculator },
  { key: "suggested", label: "Suggested Orders", icon: Package },
  { key: "verification", label: "Order Verification", icon: ShieldCheck },
  { key: "activity", label: "Activity", icon: ActivityIcon },
];

const StockPlanning = () => {
  const [tab, setTab] = useState("dashboard");
  const { currentUser } = useApp();
  const canWrite = can(currentUser, "stock_planning_write");

  return (
    <div className="space-y-4" data-testid="stock-planning-page">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg bg-yamaha-50 flex items-center justify-center">
          <Layers className="w-6 h-6 text-yamaha-700" />
        </div>
        <div>
          <h1 className="text-[24px] font-semibold text-yamaha-800 leading-none tracking-tight">
            Stock Planning
          </h1>
          <p className="text-[12.5px] text-[#747684] mt-1">
            Location-wise consumption analysis, ABCD categorisation, suggested ordering and order verification.
          </p>
        </div>
        <div className="ml-auto text-[10.5px] text-[#747684]">
          Phase 2 · Live calculations
        </div>
      </div>

      {!canWrite && (
        <div
          data-testid="sp-readonly-banner"
          className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-[12px] text-amber-900"
        >
          <b>View-only mode.</b> Only Admin and Parts Manager can upload, calculate ABCD,
          generate suggested orders, verify orders or export. You can still view all data.
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white border border-[#e2e1eb] rounded-xl overflow-hidden">
        <div className="flex border-b border-[#e2e1eb] overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`sp-tab-${t.key}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-[12.5px] font-semibold border-b-2 -mb-px transition-colors ${
                  active
                    ? "border-yamaha-600 text-yamaha-700 bg-yamaha-50/50"
                    : "border-transparent text-[#444653] hover:bg-[#faf8ff]"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>
        <div className="p-5">
          {tab === "dashboard" && <DashboardTab />}
          {tab === "consumption" && <ConsumptionTab />}
          {tab === "suggested" && <SuggestedTab />}
          {tab === "verification" && <VerificationTab />}
          {tab === "activity" && <ActivityTab />}
        </div>
      </div>
    </div>
  );
};

// ============== TAB 1: DASHBOARD ==============
const DashboardTab = () => {
  const { currentLocation } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (currentLocation && currentLocation !== "ALL") params.location_code = currentLocation;
      setData(await spDashboard(params));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [currentLocation]);

  const kpis = data?.kpis || {};

  const cards = [
    { label: "Total Parts Analysed", value: kpis.total_parts_analysed ?? 0, testid: "kpi-total-parts" },
    { label: "A Category", value: kpis.a_parts ?? 0, tone: "emerald", testid: "kpi-a" },
    { label: "B Category", value: kpis.b_parts ?? 0, tone: "yamaha", testid: "kpi-b" },
    { label: "C Category", value: kpis.c_parts ?? 0, tone: "amber", testid: "kpi-c" },
    { label: "D Dead Stock", value: kpis.d_parts ?? 0, tone: "rose", testid: "kpi-d" },
    { label: "Suggested Order Lines", value: kpis.suggested_order_lines ?? 0, testid: "kpi-suggested" },
    { label: "Excess Stock Lines", value: kpis.excess_stock_lines ?? 0, testid: "kpi-excess" },
    {
      label: "Last Consumption Upload",
      value: kpis.last_consumption_upload ? fmtDateTime(kpis.last_consumption_upload) : "—",
      subtle: true,
      testid: "kpi-last-consumption",
      hint: kpis.last_consumption_label,
    },
    {
      label: "Last Stock Upload",
      value: kpis.last_stock_upload ? fmtDateTime(kpis.last_stock_upload) : "—",
      subtle: true,
      testid: "kpi-last-stock",
      hint: kpis.last_stock_label,
    },
  ];

  return (
    <div className="space-y-5" data-testid="sp-dashboard">
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-[#747684]">
          {currentLocation && currentLocation !== "ALL"
            ? `Showing data for ${currentLocation}`
            : "Aggregated across all locations"}
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="text-[11.5px] inline-flex items-center gap-1 text-yamaha-700 hover:text-yamaha-800 font-semibold"
          data-testid="sp-dashboard-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {cards.slice(0, 5).map((k) => <KpiCard key={k.label} {...k} />)}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.slice(5).map((k) => <KpiCard key={k.label} {...k} />)}
      </div>
    </div>
  );
};

const KpiCard = ({ label, value, tone, subtle, testid }) => {
  const toneClasses = {
    emerald: "text-emerald-700",
    yamaha: "text-yamaha-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
  };
  return (
    <div className="bg-white border border-[#e2e1eb] rounded-xl px-4 py-3" data-testid={testid}>
      <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#747684]">{label}</div>
      <div className={`mt-0.5 ${subtle ? "text-[14px] font-semibold text-[#444653]" : "text-[22px] font-semibold"} ${toneClasses[tone] || "text-[#1a1b22]"}`}>
        {value}
      </div>
    </div>
  );
};

// ============== TAB 2: CONSUMPTION ANALYSIS ==============
const ConsumptionTab = () => {
  const { locations, currentUser } = useApp();
  const allowed = locations.filter(
    (l) => currentUser?.role === "admin" || currentUser?.location_codes?.includes(l.code)
  );

  const [loc, setLoc] = useState(allowed[0]?.code || "");
  const [monthYear, setMonthYear] = useState(new Date().toISOString().slice(0, 7));
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [reports, setReports] = useState([]);
  // ABCD
  const [filterLoc, setFilterLoc] = useState(allowed[0]?.code || "");
  const [filterCat, setFilterCat] = useState("ALL");
  const [filterQ, setFilterQ] = useState("");
  const [abcd, setAbcd] = useState(null);
  const [calcRunning, setCalcRunning] = useState(false);

  const loadReports = async () => {
    try { setReports(await spListConsumption()); } catch { /* swallow */ }
  };

  const loadAbcd = async () => {
    try {
      const params = {};
      if (filterLoc && filterLoc !== "ALL") params.location_code = filterLoc;
      if (filterCat && filterCat !== "ALL") params.category = filterCat;
      if (filterQ) params.q = filterQ;
      setAbcd(await spGetABCD(params));
    } catch { setAbcd(null); }
  };

  useEffect(() => { loadReports(); }, []);
  useEffect(() => { loadAbcd(); /* eslint-disable-next-line */ }, [filterLoc, filterCat, filterQ]);

  const handleUpload = async () => {
    if (!file) return toast.error("Please choose a Consumption Excel file.");
    if (!loc) return toast.error("Please select a location.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("location_code", loc);
      fd.append("month_year", monthYear);
      fd.append("actor", currentUser.name);
      fd.append("actor_role", currentUser.role);
      fd.append("file", file);
      const res = await spUploadConsumption(fd);
      toast.success(`Uploaded ${res.rows} rows for ${res.location_code} · ${res.month_year}`);
      setFile(null);
      loadReports();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteReport = async (id) => {
    if (!window.confirm("Delete this consumption report?")) return;
    try {
      await spDeleteConsumption(id, currentUser.name, currentUser.role);
      toast.success("Report deleted");
      loadReports();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const handleCalculate = async () => {
    if (!filterLoc || filterLoc === "ALL") return toast.error("Please pick a specific location to calculate ABCD.");
    setCalcRunning(true);
    try {
      await spCalculateABCD(filterLoc, currentUser.name, currentUser.role);
      toast.success("ABCD calculated");
      loadAbcd();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Calculation failed");
    } finally {
      setCalcRunning(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="sp-consumption">
      <SectionCard title="Upload Monthly Consumption Report" subtitle="Excel format: Part No · Part Name · Invoiced Qty (column names are flexible — Part Number / Description / Sale Qty also accepted)">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <LocationSelect label="Location" value={loc} onChange={setLoc} options={allowed} testid="cons-loc" />
          <Field label="Month-Year">
            <input
              type="month"
              data-testid="cons-month"
              value={monthYear}
              onChange={(e) => setMonthYear(e.target.value)}
              className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]"
            />
          </Field>
          <Field label="Consumption Excel" className="md:col-span-2">
            <FileDrop file={file} setFile={setFile} testid="cons-file" />
          </Field>
        </div>
        <div className="mt-3 flex justify-end">
          <Button
            data-testid="cons-upload-btn"
            onClick={handleUpload}
            disabled={uploading}
            className="bg-yamaha-700 hover:bg-yamaha-800"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? "Uploading…" : "Upload Consumption Report"}
          </Button>
        </div>
      </SectionCard>

      <SectionCard title="Uploaded Consumption Reports">
        <table className="w-full text-[12.5px]" data-testid="sp-cons-reports">
          <thead className="bg-[#faf8ff]">
            <tr>
              <Th>Location</Th>
              <Th>Month-Year</Th>
              <Th>File Name</Th>
              <Th>Upload Date</Th>
              <Th>Uploaded By</Th>
              <Th align="center">Action</Th>
            </tr>
          </thead>
          <tbody>
            {reports.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-[#747684]">No reports uploaded yet.</td>
              </tr>
            )}
            {reports.map((r) => (
              <tr key={r.id} className="border-t border-[#e2e1eb] hover:bg-[#faf8ff]">
                <td className="px-3 py-1.5">{r.location_code}</td>
                <td className="px-3 py-1.5 font-mono">{r.month_year}</td>
                <td className="px-3 py-1.5">{r.file_name}</td>
                <td className="px-3 py-1.5 text-[#444653]">{fmtDateTime(r.uploaded_at)}</td>
                <td className="px-3 py-1.5">{r.uploaded_by || "—"}</td>
                <td className="px-3 py-1.5 text-center">
                  <button
                    onClick={() => handleDeleteReport(r.id)}
                    className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                    title="Delete report"
                    data-testid={`cons-del-${r.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>

      <SectionCard title="ABCD Analysis" subtitle={`Uses the latest 3 months of uploaded consumption reports for the selected location.${abcd?.months?.length ? ` · Last calculated: ${abcd.months.join(", ")}` : ""}`}>
        <div className="flex flex-wrap items-end gap-3 mb-3">
          <Field label="Location" className="min-w-[180px]">
            <select value={filterLoc} onChange={(e) => setFilterLoc(e.target.value)} className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" data-testid="abcd-loc">
              {allowed.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
            </select>
          </Field>
          <Field label="Category" className="min-w-[140px]">
            <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" data-testid="abcd-cat">
              <option value="ALL">All</option>
              <option value="A">A</option>
              <option value="B">B</option>
              <option value="C">C</option>
              <option value="D">D</option>
            </select>
          </Field>
          <Field label="Search" className="flex-1 min-w-[220px]">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-[#747684] absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                value={filterQ}
                onChange={(e) => setFilterQ(e.target.value)}
                placeholder="Part No or Part Name"
                className="w-full pl-8 pr-3 py-2 border border-[#e2e1eb] rounded-md text-[13px]"
                data-testid="abcd-q"
              />
            </div>
          </Field>
          <Button
            data-testid="abcd-calc-btn"
            onClick={handleCalculate}
            disabled={calcRunning}
            className="bg-yamaha-700 hover:bg-yamaha-800"
          >
            {calcRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}
            Calculate ABCD
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="sp-abcd-table">
            <thead className="bg-[#faf8ff]">
              <tr>
                <Th>Location</Th>
                <Th>Part No</Th>
                <Th>Part Name</Th>
                <Th align="right">M1 Sale</Th>
                <Th align="right">M2 Sale</Th>
                <Th align="right">M3 Sale</Th>
                <Th align="right">Total</Th>
                <Th align="right">Avg/Mo</Th>
                <Th align="right">Cons %</Th>
                <Th align="right">Cum %</Th>
                <Th>Category</Th>
              </tr>
            </thead>
            <tbody>
              {(!abcd || !abcd.rows || abcd.rows.length === 0) && (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-[#747684]">
                    No ABCD result. Upload 3 months of consumption and click &quot;Calculate ABCD&quot;.
                  </td>
                </tr>
              )}
              {(abcd?.rows || []).map((r) => (
                <tr key={r.part_number + r.location_code} className="border-t border-[#e2e1eb] hover:bg-[#faf8ff]">
                  <td className="px-3 py-1.5">{r.location_code}</td>
                  <td className="px-3 py-1.5 font-mono text-yamaha-700">{r.part_number}</td>
                  <td className="px-3 py-1.5">{r.part_name}</td>
                  <td className="px-3 py-1.5 text-right">{r.m1}</td>
                  <td className="px-3 py-1.5 text-right">{r.m2}</td>
                  <td className="px-3 py-1.5 text-right">{r.m3}</td>
                  <td className="px-3 py-1.5 text-right font-semibold">{r.total}</td>
                  <td className="px-3 py-1.5 text-right">{r.avg}</td>
                  <td className="px-3 py-1.5 text-right">{r.consumption_pct}%</td>
                  <td className="px-3 py-1.5 text-right">{r.cumulative_pct}%</td>
                  <td className="px-3 py-1.5"><CategoryBadge cat={r.category} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
};

// ============== TAB 3: SUGGESTED ORDERS ==============
const SuggestedTab = () => {
  const { locations, currentUser } = useApp();
  const allowed = locations.filter(
    (l) => currentUser?.role === "admin" || currentUser?.location_codes?.includes(l.code)
  );
  const [loc, setLoc] = useState(allowed[0]?.code || "");
  const [stockDate, setStockDate] = useState(new Date().toISOString().slice(0, 10));
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [fLoc, setFLoc] = useState(allowed[0]?.code || "");
  const [fCat, setFCat] = useState("ALL");
  const [fReq, setFReq] = useState("ALL");
  const [fQ, setFQ] = useState("");
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const params = {};
      if (fLoc && fLoc !== "ALL") params.location_code = fLoc;
      if (fCat && fCat !== "ALL") params.category = fCat;
      if (fReq !== "ALL") params.order_required = fReq;
      if (fQ) params.q = fQ;
      setData(await spGetSuggested(params));
    } catch { setData(null); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fLoc, fCat, fReq, fQ]);

  const handleUpload = async () => {
    if (!file) return toast.error("Please choose a Current Stock Excel file.");
    if (!loc) return toast.error("Please select a location.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("location_code", loc);
      fd.append("stock_date", stockDate);
      fd.append("actor", currentUser.name);
      fd.append("actor_role", currentUser.role);
      fd.append("file", file);
      const res = await spGenerateSuggested(fd);
      toast.success(`Generated ${res.totals?.total_lines || 0} suggested order lines`);
      setFile(null);
      setFLoc(loc);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setUploading(false);
    }
  };

  const exportUrl = useMemo(() => {
    const p = {};
    if (fLoc && fLoc !== "ALL") p.location_code = fLoc;
    if (fCat && fCat !== "ALL") p.category = fCat;
    if (fReq !== "ALL") p.order_required = fReq;
    if (fQ) p.q = fQ;
    return p; // now returns the params dict; blob download uses it
  }, [fLoc, fCat, fReq, fQ]);

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const resp = await spExportSuggested(exportUrl);
      const blob = new Blob([resp.data], { type: resp.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = url;
      a.download = `suggested-orders-${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Exported suggested orders");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="sp-suggested">
      <SectionCard title="Upload Current Stock Report" subtitle="Excel format: Part No · Part Name · Current Stock Qty (flexible — Part Number / Description / Closing Stock also accepted)">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <LocationSelect label="Location" value={loc} onChange={setLoc} options={allowed} testid="sug-loc" />
          <Field label="Stock Date">
            <input
              type="date"
              data-testid="sug-stock-date"
              value={stockDate}
              onChange={(e) => setStockDate(e.target.value)}
              className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]"
            />
          </Field>
          <Field label="Current Stock Excel" className="md:col-span-2">
            <FileDrop file={file} setFile={setFile} testid="sug-file" />
          </Field>
        </div>
        <div className="mt-3 flex justify-end">
          <Button
            data-testid="sug-upload-btn"
            onClick={handleUpload}
            disabled={uploading}
            className="bg-yamaha-700 hover:bg-yamaha-800"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? "Generating…" : "Upload Stock & Generate Suggested Order"}
          </Button>
        </div>
      </SectionCard>

      <SectionCard title="Coverage Rules">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
          <CoverageCard cat="A" rule="1.5 months coverage" tone="emerald" />
          <CoverageCard cat="B" rule="1 month coverage" tone="yamaha" />
          <CoverageCard cat="C" rule="Order only against demand" tone="amber" />
          <CoverageCard cat="D" rule="Do not order (dead stock)" tone="rose" />
        </div>
      </SectionCard>

      <SectionCard title="Suggested Order Lines">
        <div className="flex flex-wrap items-end gap-3 mb-3">
          <Field label="Location" className="min-w-[160px]">
            <select value={fLoc} onChange={(e) => setFLoc(e.target.value)} className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" data-testid="sug-f-loc">
              <option value="ALL">All</option>
              {allowed.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
            </select>
          </Field>
          <Field label="Category" className="min-w-[120px]">
            <select value={fCat} onChange={(e) => setFCat(e.target.value)} className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" data-testid="sug-f-cat">
              <option value="ALL">All</option>
              <option>A</option><option>B</option><option>C</option><option>D</option>
            </select>
          </Field>
          <Field label="Order Required" className="min-w-[140px]">
            <select value={fReq} onChange={(e) => setFReq(e.target.value)} className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" data-testid="sug-f-req">
              <option value="ALL">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </Field>
          <Field label="Search" className="flex-1 min-w-[220px]">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-[#747684] absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                value={fQ}
                onChange={(e) => setFQ(e.target.value)}
                placeholder="Part No or Part Name"
                className="w-full pl-8 pr-3 py-2 border border-[#e2e1eb] rounded-md text-[13px]"
                data-testid="sug-f-q"
              />
            </div>
          </Field>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            data-testid="sug-export-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#cfcdd8] hover:bg-yamaha-50 disabled:opacity-50 text-[12.5px] font-semibold text-[#1a1b22]"
          >
            <Download className="w-4 h-4" />
            {exporting ? "Exporting…" : "Export Suggested Orders"}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="sp-sug-table">
            <thead className="bg-[#faf8ff]">
              <tr>
                <Th>Location</Th>
                <Th>Part No</Th>
                <Th>Part Name</Th>
                <Th>Cat</Th>
                <Th align="right">Avg/Mo</Th>
                <Th align="right">Stock</Th>
                <Th align="right">Cover (mo)</Th>
                <Th align="right">Required</Th>
                <Th align="right">Suggest</Th>
                <Th align="right">Excess</Th>
                <Th>Remarks</Th>
              </tr>
            </thead>
            <tbody>
              {(!data || !data.rows || data.rows.length === 0) && (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-[#747684]">
                    No suggested orders yet. Upload a Current Stock Excel above to generate.
                  </td>
                </tr>
              )}
              {(data?.rows || []).map((r) => (
                <tr key={r.location_code + r.part_number} className="border-t border-[#e2e1eb] hover:bg-[#faf8ff]">
                  <td className="px-3 py-1.5">{r.location_code}</td>
                  <td className="px-3 py-1.5 font-mono text-yamaha-700">{r.part_number}</td>
                  <td className="px-3 py-1.5">{r.part_name}</td>
                  <td className="px-3 py-1.5"><CategoryBadge cat={r.category} /></td>
                  <td className="px-3 py-1.5 text-right">{r.avg}</td>
                  <td className="px-3 py-1.5 text-right">{r.current_stock}</td>
                  <td className="px-3 py-1.5 text-right">{r.coverage_months}</td>
                  <td className="px-3 py-1.5 text-right">{r.required_stock}</td>
                  <td className="px-3 py-1.5 text-right font-semibold text-yamaha-700">{r.suggested_order_qty}</td>
                  <td className="px-3 py-1.5 text-right">{r.excess_stock}</td>
                  <td className="px-3 py-1.5 text-[11px] text-[#747684]">{r.remarks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
};

// ============== TAB 4: ORDER VERIFICATION ==============
const VerificationTab = () => {
  const { locations, currentUser } = useApp();
  const allowed = locations.filter(
    (l) => currentUser?.role === "admin" || currentUser?.location_codes?.includes(l.code)
  );
  const [loc, setLoc] = useState(allowed[0]?.code || "");
  const [stockDate, setStockDate] = useState(new Date().toISOString().slice(0, 10));
  const [orderDate, setOrderDate] = useState(new Date().toISOString().slice(0, 10));
  const [stockFile, setStockFile] = useState(null);
  const [orderFile, setOrderFile] = useState(null);
  const [running, setRunning] = useState(false);

  const [fLoc, setFLoc] = useState(allowed[0]?.code || "");
  const [fStatus, setFStatus] = useState("ALL");
  const [fQ, setFQ] = useState("");
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const params = {};
      if (fLoc && fLoc !== "ALL") params.location_code = fLoc;
      if (fStatus !== "ALL") params.status = fStatus;
      if (fQ) params.q = fQ;
      setData(await spGetVerification(params));
    } catch { setData(null); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fLoc, fStatus, fQ]);

  const handleVerify = async () => {
    if (!stockFile) return toast.error("Please choose the Current Stock Excel file.");
    if (!orderFile) return toast.error("Please choose the Proposed Order Excel file.");
    if (!loc) return toast.error("Please select a location.");
    setRunning(true);
    try {
      const fd = new FormData();
      fd.append("location_code", loc);
      fd.append("stock_date", stockDate);
      fd.append("order_date", orderDate);
      fd.append("actor", currentUser.name);
      fd.append("actor_role", currentUser.role);
      fd.append("stock_file", stockFile);
      fd.append("order_file", orderFile);
      const res = await spVerifyOrder(fd);
      toast.success(`Verified ${res.rows?.length || 0} order lines · OK:${res.totals.ok} Excess:${res.totals.excess} Under:${res.totals.under} Transfer:${res.totals.transfer_available || 0}`);
      setStockFile(null); setOrderFile(null);
      setFLoc(loc);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally {
      setRunning(false);
    }
  };

  const exportParams = useMemo(() => {
    const p = {};
    if (fLoc && fLoc !== "ALL") p.location_code = fLoc;
    if (fStatus !== "ALL") p.status = fStatus;
    if (fQ) p.q = fQ;
    return p;
  }, [fLoc, fStatus, fQ]);

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const resp = await spExportVerification(exportParams);
      const blob = new Blob([resp.data], { type: resp.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = url;
      a.download = `order-verification-${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Exported verification report");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="sp-verification">
      <SectionCard title="Upload Files for Order Verification" subtitle="Current Stock Excel: Part No · Part Name · Current Stock Qty   |   Proposed Order Excel: Part No · Part Name · Order Qty">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <LocationSelect label="Location" value={loc} onChange={setLoc} options={allowed} testid="ver-loc" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Stock Date">
              <input
                type="date"
                data-testid="ver-stock-date"
                value={stockDate}
                onChange={(e) => setStockDate(e.target.value)}
                className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]"
              />
            </Field>
            <Field label="Order Date">
              <input
                type="date"
                data-testid="ver-order-date"
                value={orderDate}
                onChange={(e) => setOrderDate(e.target.value)}
                className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]"
              />
            </Field>
          </div>
          <Field label="Current Stock Excel">
            <FileDrop file={stockFile} setFile={setStockFile} testid="ver-stock-file" />
          </Field>
          <Field label="Proposed Order Excel">
            <FileDrop file={orderFile} setFile={setOrderFile} testid="ver-order-file" />
          </Field>
        </div>
        <div className="mt-3 flex justify-end">
          <Button
            data-testid="ver-run-btn"
            onClick={handleVerify}
            disabled={running}
            className="bg-yamaha-700 hover:bg-yamaha-800"
          >
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
            {running ? "Verifying…" : "Verify Uploaded Order"}
          </Button>
        </div>
      </SectionCard>

      <SectionCard title="Verification Results">
        <div className="flex flex-wrap items-end gap-3 mb-3">
          <Field label="Location" className="min-w-[160px]">
            <select value={fLoc} onChange={(e) => setFLoc(e.target.value)} className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" data-testid="ver-f-loc">
              <option value="ALL">All</option>
              {allowed.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
            </select>
          </Field>
          <Field label="Status" className="min-w-[180px]">
            <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" data-testid="ver-f-status">
              <option value="ALL">All</option>
              <option value="ok">OK</option>
              <option value="excess">Excess Order</option>
              <option value="under">Under Ordered</option>
              <option value="c-cat">C Category</option>
              <option value="d-cat">D Category</option>
              <option value="not-required">Not Required</option>
              <option value="transfer-available">Transfer Available</option>
            </select>
          </Field>
          <Field label="Search" className="flex-1 min-w-[220px]">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-[#747684] absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                value={fQ}
                onChange={(e) => setFQ(e.target.value)}
                placeholder="Part No or Part Name"
                className="w-full pl-8 pr-3 py-2 border border-[#e2e1eb] rounded-md text-[13px]"
                data-testid="ver-f-q"
              />
            </div>
          </Field>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            data-testid="ver-export-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#cfcdd8] hover:bg-yamaha-50 disabled:opacity-50 text-[12.5px] font-semibold text-[#1a1b22]"
          >
            <Download className="w-4 h-4" />
            {exporting ? "Exporting…" : "Export Verification Report"}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="sp-ver-table">
            <thead className="bg-[#faf8ff]">
              <tr>
                <Th>Location</Th>
                <Th>Part No</Th>
                <Th>Part Name</Th>
                <Th>Cat</Th>
                <Th align="right">Stock</Th>
                <Th align="right">Suggested</Th>
                <Th align="right">Uploaded</Th>
                <Th align="right">Diff</Th>
                <Th>Status</Th>
                <Th>Remarks</Th>
              </tr>
            </thead>
            <tbody>
              {(!data || !data.rows || data.rows.length === 0) && (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-[#747684]">
                    No verification results yet. Upload current stock + proposed order above to verify.
                  </td>
                </tr>
              )}
              {(data?.rows || []).map((r) => (
                <tr key={r.location_code + r.part_number} className="border-t border-[#e2e1eb] hover:bg-[#faf8ff]">
                  <td className="px-3 py-1.5">{r.location_code}</td>
                  <td className="px-3 py-1.5 font-mono text-yamaha-700">{r.part_number}</td>
                  <td className="px-3 py-1.5">{r.part_name}</td>
                  <td className="px-3 py-1.5"><CategoryBadge cat={r.category} /></td>
                  <td className="px-3 py-1.5 text-right">{r.current_stock}</td>
                  <td className="px-3 py-1.5 text-right">{r.suggested_qty}</td>
                  <td className="px-3 py-1.5 text-right">{r.uploaded_order_qty}</td>
                  <td className={`px-3 py-1.5 text-right font-semibold ${
                    r.difference_qty > 0 ? "text-emerald-700" : r.difference_qty < 0 ? "text-rose-700" : "text-[#747684]"
                  }`}>{r.difference_qty > 0 ? `+${r.difference_qty}` : r.difference_qty}</td>
                  <td className="px-3 py-1.5"><StatusBadge status={r.status} /></td>
                  <td className="px-3 py-1.5 text-[11px] text-[#747684]">{r.remarks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
};

// ============== TAB 5: ACTIVITY ==============
const ACTIVITY_TYPES = [
  { value: "CONSUMPTION_UPLOADED", label: "Consumption Upload" },
  { value: "ABCD_CALCULATED", label: "ABCD Calculation" },
  { value: "SUGGESTED_ORDERS_GENERATED", label: "Suggested Order Generation" },
  { value: "ORDER_VERIFIED", label: "Order Verification" },
];

const ActivityTab = () => {
  const { locations, currentUser, currentLocation } = useApp();
  const allowed = locations.filter(
    (l) => currentUser?.role === "admin" || currentUser?.location_codes?.includes(l.code)
  );

  // Filters — initialise location filter from the global location chip
  // so the user lands on the data they're already focused on.
  const initialLoc =
    currentLocation && currentLocation !== "ALL" && !currentLocation.includes(",")
      ? currentLocation
      : "ALL";
  const [fLoc, setFLoc] = useState(initialLoc);
  const [fType, setFType] = useState("ALL");
  const [fUser, setFUser] = useState("ALL");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");

  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerRow, setDrawerRow] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fLoc && fLoc !== "ALL") params.location_code = fLoc;
      if (fType && fType !== "ALL") params.activity_type = fType;
      if (fUser && fUser !== "ALL") params.user = fUser;
      if (fFrom) params.from_date = fFrom;
      if (fTo) params.to_date = fTo;
      const res = await spActivity(params);
      setRows(res?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load activity");
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const params = {};
      if (fLoc && fLoc !== "ALL") params.location_code = fLoc;
      setUsers(await spActivityUsers(params));
    } catch {
      setUsers([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [fLoc, fType, fUser, fFrom, fTo]);

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line
  }, [fLoc]);

  const resetFilters = () => {
    setFLoc("ALL"); setFType("ALL"); setFUser("ALL");
    setFFrom(""); setFTo("");
  };

  return (
    <div className="space-y-4" data-testid="sp-activity">
      {/* Filters bar */}
      <div className="bg-white border border-[#e2e1eb] rounded-xl p-3 flex flex-wrap items-end gap-2.5">
        <div className="flex items-center gap-1.5 text-[#747684] pb-2">
          <Filter className="w-3.5 h-3.5" />
          <span className="text-[12px] font-semibold text-[#444653]">Filters:</span>
        </div>
        <Field label="Location" className="min-w-[150px]">
          <select
            data-testid="act-f-loc"
            value={fLoc}
            onChange={(e) => setFLoc(e.target.value)}
            className="w-full border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12.5px]"
          >
            <option value="ALL">All</option>
            {allowed.map((l) => (
              <option key={l.code} value={l.code}>{l.name} ({l.code})</option>
            ))}
          </select>
        </Field>
        <Field label="Activity Type" className="min-w-[200px]">
          <select
            data-testid="act-f-type"
            value={fType}
            onChange={(e) => setFType(e.target.value)}
            className="w-full border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12.5px]"
          >
            <option value="ALL">All</option>
            {ACTIVITY_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </Field>
        <Field label="User" className="min-w-[180px]">
          <select
            data-testid="act-f-user"
            value={fUser}
            onChange={(e) => setFUser(e.target.value)}
            className="w-full border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12.5px]"
          >
            <option value="ALL">All</option>
            {users.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </Field>
        <Field label="From" className="min-w-[140px]">
          <input
            type="date"
            data-testid="act-f-from"
            value={fFrom}
            onChange={(e) => setFFrom(e.target.value)}
            className="w-full border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12.5px]"
          />
        </Field>
        <Field label="To" className="min-w-[140px]">
          <input
            type="date"
            data-testid="act-f-to"
            value={fTo}
            onChange={(e) => setFTo(e.target.value)}
            className="w-full border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12.5px]"
          />
        </Field>
        <button
          onClick={resetFilters}
          data-testid="act-f-reset"
          className="pb-2 text-[11.5px] text-yamaha-700 hover:text-yamaha-800 font-semibold"
        >
          Reset
        </button>
        <div className="ml-auto pb-2 text-[11px] text-[#747684]">
          {loading ? "Loading…" : `${rows.length} activit${rows.length === 1 ? "y" : "ies"}`}
        </div>
      </div>

      {/* Activity table */}
      <div className="bg-white border border-[#e2e1eb] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="sp-activity-full-table">
            <thead className="bg-[#faf8ff]">
              <tr>
                <Th>Date &amp; Time</Th>
                <Th>Location</Th>
                <Th>Activity Type</Th>
                <Th>User</Th>
                <Th>Analysis Period</Th>
                <Th>Summary</Th>
                <Th align="center">Action</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-[#747684]">
                    No activities found with the current filters.
                  </td>
                </tr>
              )}
              {rows.map((r, i) => (
                <ActivityRow
                  key={r.id || i}
                  row={r}
                  onView={() => setDrawerRow(r)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ActivityDrawer row={drawerRow} onClose={() => setDrawerRow(null)} />
    </div>
  );
};

// ============== ACTIVITY ROW + DRAWER (shared by Dashboard & Activity tab) ==============

const ACTIVITY_TYPE_TONES = {
  CONSUMPTION_UPLOADED:
    "bg-yamaha-50 text-yamaha-700 border-yamaha-200",
  ABCD_CALCULATED:
    "bg-emerald-50 text-emerald-700 border-emerald-200",
  SUGGESTED_ORDERS_GENERATED:
    "bg-amber-50 text-amber-700 border-amber-200",
  ORDER_VERIFIED:
    "bg-violet-50 text-violet-700 border-violet-200",
};

const ActivityTypePill = ({ type, label }) => (
  <span
    className={`inline-block text-[10.5px] font-bold uppercase tracking-wider border rounded-full px-2 py-0.5 ${
      ACTIVITY_TYPE_TONES[type] || "bg-[#f4f3fc] text-[#444653] border-[#e2e1eb]"
    }`}
  >
    {label || (type || "—").replace(/_/g, " ")}
  </span>
);

const ActivityRow = ({ row, onView }) => (
  <tr className="border-t border-[#e2e1eb] hover:bg-[#faf8ff]">
    <td className="px-3 py-1.5 text-[#444653] whitespace-nowrap">
      {row.date ? fmtDateTime(row.date) : "—"}
    </td>
    <td className="px-3 py-1.5 font-mono text-[12px]">{row.location}</td>
    <td className="px-3 py-1.5">
      <ActivityTypePill type={row.activity_type} label={row.activity_type_label} />
    </td>
    <td className="px-3 py-1.5">{row.user}</td>
    <td className="px-3 py-1.5 text-[11.5px] text-[#444653]">{row.analysis_period_label}</td>
    <td className="px-3 py-1.5 text-[#1a1b22]">{row.summary}</td>
    <td className="px-3 py-1.5 text-center">
      <button
        onClick={onView}
        data-testid={`activity-view-${row.id || row.target_id}`}
        title="View details"
        className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11.5px] text-yamaha-700 hover:bg-yamaha-50 font-semibold"
      >
        <Eye className="w-3 h-3" /> View
      </button>
    </td>
  </tr>
);

const ActivityDrawer = ({ row, onClose }) => {
  const d = row?.drawer || {};
  const kind = d.kind;

  // Memoise each drawer's items list — they change only when the selected
  // activity row changes, so we avoid handing DetailGrid a fresh reference
  // on incidental re-renders (e.g. sheet open/close transitions).
  const consumptionItems = useMemo(
    () => kind === "CONSUMPTION_UPLOADED"
      ? [
          ["Location", d.location],
          ["Uploaded Month", d.uploaded_month],
          ["Upload Date", d.upload_date ? fmtDateTime(d.upload_date) : "—"],
          ["Uploaded By", d.uploaded_by],
          ["File Name", d.file_name],
          ...(d.row_count != null ? [["Rows", String(d.row_count)]] : []),
        ]
      : [],
    [kind, d.location, d.uploaded_month, d.upload_date, d.uploaded_by, d.file_name, d.row_count],
  );

  const abcdItems = useMemo(
    () => kind === "ABCD_CALCULATED"
      ? [
          ["Location", d.location],
          ["Analysis Period", d.analysis_period],
          ["Run Date", d.run_date ? fmtDateTime(d.run_date) : "—"],
          ["Run By", d.run_by],
          ["Total Parts Analysed", numberFmt(d.total_parts)],
        ]
      : [],
    [kind, d.location, d.analysis_period, d.run_date, d.run_by, d.total_parts],
  );

  const suggestedItems = useMemo(
    () => kind === "SUGGESTED_ORDERS_GENERATED"
      ? [
          ["Location", d.location],
          ["Analysis Period", d.analysis_period],
          ["Run Date", d.run_date ? fmtDateTime(d.run_date) : "—"],
          ["Run By", d.run_by],
          ["Total Parts Analysed", numberFmt(d.total_parts_analysed)],
          ["Suggested Order Lines", numberFmt(d.suggested_order_lines)],
          ["Total Suggested Quantity", numberFmt(d.total_suggested_qty)],
          ["Stock File Name", d.stock_file_name],
        ]
      : [],
    [kind, d.location, d.analysis_period, d.run_date, d.run_by,
     d.total_parts_analysed, d.suggested_order_lines, d.total_suggested_qty, d.stock_file_name],
  );

  const verificationItems = useMemo(
    () => kind === "ORDER_VERIFIED"
      ? [
          ["Location", d.location],
          ["Analysis Period", d.analysis_period],
          ["Run Date", d.run_date ? fmtDateTime(d.run_date) : "—"],
          ["Run By", d.run_by],
          ["Verified Order Lines", numberFmt(d.verified_order_lines)],
          ["Excess Order Lines", numberFmt(d.excess_order_lines)],
          ["Under Ordered Lines", numberFmt(d.under_ordered_lines)],
          ["C Category Ordered", numberFmt(d.c_category_ordered)],
          ["D Category Ordered", numberFmt(d.d_category_ordered)],
        ]
      : [],
    [kind, d.location, d.analysis_period, d.run_date, d.run_by,
     d.verified_order_lines, d.excess_order_lines, d.under_ordered_lines,
     d.c_category_ordered, d.d_category_ordered],
  );

  return (
    <Sheet open={!!row} onOpenChange={(open) => !open && onClose && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto"
        data-testid="activity-drawer"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-yamaha-700" />
            Activity Details
          </SheetTitle>
          <SheetDescription>
            {row?.activity_type_label || "—"}
          </SheetDescription>
        </SheetHeader>

        {!row && null}
        {row && (
          <div className="mt-5 space-y-3" data-testid="activity-drawer-body">
            <div className="flex items-center gap-2">
              <ActivityTypePill type={row.activity_type} label={row.activity_type_label} />
              <span className="text-[11px] text-[#747684]">
                {row.date ? fmtDateTime(row.date) : "—"}
              </span>
            </div>

            {kind === "CONSUMPTION_UPLOADED" && (
              <DetailGrid items={consumptionItems} />
            )}

            {kind === "ABCD_CALCULATED" && (
              <>
                <DetailGrid items={abcdItems} />
                <div className="grid grid-cols-2 gap-2">
                  <CountStat label="A Category" value={d.a_count} tone="emerald" />
                  <CountStat label="B Category" value={d.b_count} tone="yamaha" />
                  <CountStat label="C Category" value={d.c_count} tone="amber" />
                  <CountStat label="D Category" value={d.d_count} tone="rose" />
                </div>
              </>
            )}

            {kind === "SUGGESTED_ORDERS_GENERATED" && (
              <DetailGrid items={suggestedItems} />
            )}

            {kind === "ORDER_VERIFIED" && (
              <DetailGrid items={verificationItems} />
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

const DetailGrid = ({ items }) => (
  <div className="bg-[#faf8ff] border border-[#ece9f5] rounded-lg overflow-hidden">
    {items.map(([k, v], i) => (
      <div
        key={k}
        className={`flex items-start gap-3 px-3 py-2 ${
          i === items.length - 1 ? "" : "border-b border-[#ece9f5]"
        }`}
      >
        <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#747684] w-[42%] flex-shrink-0">
          {k}
        </div>
        <div className="text-[12.5px] text-[#1a1b22] break-words">
          {v == null || v === "" ? "—" : v}
        </div>
      </div>
    ))}
  </div>
);

const CountStat = ({ label, value, tone }) => {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50/40 text-emerald-700",
    yamaha: "border-yamaha-200 bg-yamaha-50/40 text-yamaha-700",
    amber: "border-amber-200 bg-amber-50/40 text-amber-700",
    rose: "border-rose-200 bg-rose-50/40 text-rose-700",
  };
  return (
    <div className={`border rounded-lg px-3 py-2 ${tones[tone] || ""}`}>
      <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#747684]">{label}</div>
      <div className="text-[20px] font-semibold mt-0.5">{numberFmt(value)}</div>
    </div>
  );
};

const numberFmt = (n) => {
  if (n == null || n === "") return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n);
  return num.toLocaleString("en-IN");
};

// ============== SHARED COMPONENTS ==============

const SectionCard = ({ title, subtitle, children }) => (
  <div className="bg-white border border-[#e2e1eb] rounded-xl overflow-hidden">
    <div className="px-4 py-2.5 border-b border-[#e2e1eb]">
      <div className="text-[13px] font-semibold text-[#1a1b22]">{title}</div>
      {subtitle && <div className="text-[11px] text-[#747684] mt-0.5">{subtitle}</div>}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">
      {label}
    </label>
    {children}
  </div>
);

const LocationSelect = ({ label, value, onChange, options, testid }) => (
  <Field label={label}>
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]"
    >
      {options.length === 0 && <option value="">No locations</option>}
      {options.map((l) => (
        <option key={l.code} value={l.code}>{l.name} ({l.code})</option>
      ))}
    </select>
  </Field>
);

const FileDrop = ({ file, setFile, testid }) => {
  const inputRef = React.useRef(null);
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="flex-1 border-2 border-dashed border-[#cfcdd8] rounded-md px-3 py-2 text-[12px] text-left hover:border-yamaha-400 hover:bg-yamaha-50/30 transition-colors"
        data-testid={`${testid}-trigger`}
      >
        <div className="flex items-center gap-2 text-[#747684]">
          <FileSpreadsheet className="w-3.5 h-3.5" />
          <span className="truncate">{file ? file.name : "Choose .xlsx / .csv file"}</span>
        </div>
      </button>
      {file && (
        <button
          type="button"
          onClick={() => { setFile(null); if (inputRef.current) inputRef.current.value = ""; }}
          className="p-2 rounded hover:bg-rose-50 text-rose-600"
          data-testid={`${testid}-clear`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        className="hidden"
        data-testid={testid}
        onChange={(e) => setFile(e.target.files?.[0] || null)}
      />
    </div>
  );
};

const Th = ({ children, align = "left" }) => (
  <th className={`px-3 py-2 text-[10.5px] font-bold uppercase tracking-wider text-[#747684] ${
    align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left"
  }`}>
    {children}
  </th>
);

const CategoryBadge = ({ cat }) => {
  const tones = {
    A: "bg-emerald-50 text-emerald-700 border-emerald-200",
    B: "bg-yamaha-50 text-yamaha-700 border-yamaha-200",
    C: "bg-amber-50 text-amber-700 border-amber-200",
    D: "bg-rose-50 text-rose-700 border-rose-200",
  };
  return (
    <span className={`inline-block text-[10.5px] font-bold uppercase tracking-wider border rounded-full px-2 py-0.5 ${tones[cat] || "bg-[#f4f3fc] text-[#444653] border-[#e2e1eb]"}`}>
      {cat || "—"}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    OK: "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Excess Order": "bg-amber-50 text-amber-700 border-amber-200",
    "Under Ordered": "bg-rose-50 text-rose-700 border-rose-200",
    "C Category": "bg-amber-50 text-amber-700 border-amber-200",
    "D Category": "bg-rose-50 text-rose-700 border-rose-200",
    "Not Required": "bg-[#f4f3fc] text-[#444653] border-[#e2e1eb]",
    "Transfer Available": "bg-sky-50 text-sky-700 border-sky-200",
  };
  return (
    <span className={`inline-block text-[10.5px] font-bold uppercase tracking-wider border rounded-full px-2 py-0.5 ${map[status] || "bg-[#f4f3fc] text-[#444653] border-[#e2e1eb]"}`}>
      {status}
    </span>
  );
};

const CoverageCard = ({ cat, rule, tone }) => {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50/40",
    yamaha: "border-yamaha-200 bg-yamaha-50/40",
    amber: "border-amber-200 bg-amber-50/40",
    rose: "border-rose-200 bg-rose-50/40",
  };
  return (
    <div className={`border rounded-lg px-3 py-2 ${tones[tone] || "border-[#e2e1eb]"}`}>
      <div className="flex items-center gap-2">
        <CategoryBadge cat={cat} />
        <span className="text-[11.5px] text-[#1a1b22] font-medium">{rule}</span>
      </div>
    </div>
  );
};

const PhaseHint = () => null;

// ============== SAMPLE / PLACEHOLDER ROWS — removed in Phase 2 ==============

const SAMPLE_ABCD = [];
const SAMPLE_SUGGESTED = [];
const SAMPLE_VERIFICATION = [];

export default StockPlanning;
